import React, { useCallback, useRef } from "react";
import Training from "../Training/Training";
import { useParams, useHistory } from "react-router-dom";
import { Api } from "../../api";
import Assignment from "../../types/Assignment";
import { TrainingTopic } from "../../types/TrainingTopic";
import { TrainingTopicStatus, TrainingAssessmentStatus } from "../../types/TrainingTopicStatus";
import { Container, Button } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/Check";
import AppToolbar from "../AppToolbar";
import { hasError } from "../../helpers/ResponseHelper";
import ReactCanvasConfetti from "react-canvas-confetti";
import { IProps } from "react-canvas-confetti";

type CreateConfetti = NonNullable<Parameters<NonNullable<IProps["refConfetti"]>>[0]>;

interface Params {
  assignmentId: string;
}

export default function AssignmentPlayer() {
  const { assignmentId } = useParams<Params>();
  const [assignment, setAssignment] = React.useState<Assignment>();
  const [topicMap, setTopicMap] = React.useState<TrainingTopicStatus[]>([]);
  const [assignmentDone, setAssignmentDone] = React.useState(false);
  const history = useHistory();
  const refAnimationInstance = useRef<CreateConfetti | null>(null);

  const getInstance = useCallback((instance) => {
    refAnimationInstance.current = instance;
  }, []);

  React.useEffect(() => {
    if (assignmentId) {
      Api.assignment.get(assignmentId).then((result) => {
        if (result.data) {
          if (!hasError(result)) {
            setAssignment(result.data.data);
          }
        }
      });
    }
  }, [assignmentId]);

  React.useEffect(() => {
    if (assignment) {
      let a: TrainingTopicStatus[] = [];
      let training = assignment.training;
      if (training.topics) {
        for (let topic of training.topics) {
          a[topic.trainingTopicId] = {
            topicId: topic.trainingTopicId,
            topicType: topic.contentType,
            complete: false,
          } as TrainingTopicStatus;
        }
      }
      if (assignment.trainingStatus && assignment.trainingStatus.topics) {
        let topics = assignment.trainingStatus.topics;
        for (let topic of topics) {
          a[topic.topicId] = topic;
        }
      }
      setTopicMap(a);
    }
  }, [assignment]);

  function topicDoneHandler(topic: TrainingTopic, assessmentStatus?: TrainingAssessmentStatus) {
    let topicUpdate = {
      topicId: topic.trainingTopicId,
      complete: true,
      topicType: topic.contentType,
      score: assessmentStatus?.score,
      questions: assessmentStatus?.questions,
    } as TrainingTopicStatus;
    let map = { ...topicMap };
    map[topicUpdate.topicId] = topicUpdate;
    setTopicMap(map);
    updateAssignment(map);
  }

  /*
  This is just to send completed topic to backend
  */
  function updateTopicComplete(topic: TrainingTopic, assessmentStatus?: TrainingAssessmentStatus) {
    let topicUpdate = {
      topicId: topic.trainingTopicId,
      complete: true,
      topicType: topic.contentType,
      score: assessmentStatus?.score,
      questions: assessmentStatus?.questions,
    } as TrainingTopicStatus;
    let map = { ...topicMap };
    map[topicUpdate.topicId] = topicUpdate;
    updateAssignment(map);
  }

  function updateAssignment(map: TrainingTopicStatus[]) {
    let a: TrainingTopicStatus[] = [];
    for (let id in map) {
      a.push(map[id]);
    }
    let updatedAssignment = assignment;
    if (updatedAssignment) {
      updatedAssignment.trainingStatus = {
        status: { currentPosition: 1 },
        topics: a,
      };

      setAssignment(updatedAssignment);
      Api.assignment.save(updatedAssignment).then((result) => {
        console.log(result);
      });
    }
  }

  function updateVideoStatus(topic: TrainingTopic, time: number) {
    let topicUpdate = {
      topicId: topic.trainingTopicId,
      complete: false,
      topicType: topic.contentType,
      currentTime: time,
    } as TrainingTopicStatus;
    let map = { ...topicMap };
    map[topicUpdate.topicId] = topicUpdate;
    updateAssignment(map);
  }

  const makeShot = useCallback((particleRatio, opts) => {
    refAnimationInstance.current?.({
      ...opts,
      origin: { y: 0.7 },
      particleCount: Math.floor(200 * particleRatio),
    });
  }, []);

  function trainingDone() {
    if (assignmentId && !assignmentDone) {
      Api.assignment.complete(assignmentId).then((result) => {
        if (!hasError(result)) {
          setAssignmentDone(true);
          playConfetti();
        }
      });
    }
  }

  function playConfetti() {
    makeShot(0.25, {
      spread: 26,
      startVelocity: 55,
    });

    makeShot(0.2, {
      spread: 60,
    });

    makeShot(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });

    makeShot(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });

    makeShot(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  }

  function getContent() {
    if (assignment) {
      switch (assignment.assignmentType.toLocaleUpperCase()) {
        case "TRAINING":
          return (
            <Training
              trainingId={assignment.training.trainingId}
              trainingVersion={assignment.training.trainingVersion}
              onTopicDone={topicDoneHandler}
              onUpdateTopic={updateTopicComplete}
              status={topicMap}
              updateVideoStatus={updateVideoStatus}
              onTrainingDone={trainingDone}
            />
          );
        default:
          return null;
      }
    }
    return null;
  }

  return (
    <>
      {assignmentDone ? (
        <>
          <AppToolbar />
          <div className="accent-container">
            <Container maxWidth="md" style={{ textAlign: "center" }}>
              <CheckCircleIcon style={{ width: 200, height: 200 }} />
              <h1>Congratulations!</h1>
              <h2 style={{ marginTop: 50 }}>You have completed</h2>
              <h2>{assignment?.assignmentName}</h2>

              <ReactCanvasConfetti
                refConfetti={getInstance}
                style={{ position: "fixed", pointerEvents: "none", width: "100%", height: "100%", top: 0, left: 0 }}
                useWorker={true}
              />

              <Button
                color="default"
                onClick={() => history.push("/")}
                style={{ marginTop: 30, padding: 20 }}
                size="large"
              >
                Go to your Dashboard
              </Button>
            </Container>
          </div>
        </>
      ) : (
        getContent()
      )}
    </>
  );
}
